import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import { IResOrder, IResOrders, Order } from '@/types/plugins/mlParcel';

export interface IFilter {
  startDate: string;
  endDate: string;
  status: string;
}

const initialFilters: IFilter = { startDate: '', endDate: '', status: '' };

interface OrdersState {
  data: Order[];
  total: number;
  loading: boolean;
  error: boolean;
}

interface ModalState {
  details: boolean;
  downloadLabel: boolean;
}

const useMLParcel = () => {
  const { data: profile } = useSelector((state: RootState) => state.parcel.profile);
  const clientId = profile?.credentials.clientId ?? '';

  const pagination = useServerPagination();
  const [orders, setOrders] = useState<OrdersState>({
    data: [],
    total: 0,
    loading: false,
    error: false,
  });
  const [modals, setModals] = useState<ModalState>({ details: false, downloadLabel: false });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedTrackingIds, setSelectedTrackingIds] = useState<string[]>([]);

  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFilter>(initialFilters);
  const [searchValue, setSearchValue] = useState('');

  const handleModal = (name: keyof ModalState) => {
    setModals((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const onChangeFilter = useCallback((filterName: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [filterName as keyof IFilter]: value ?? '' }));
  }, []);

  // Fetch paginated list — status/dates are server-side params
  const fetchOrders = async (activeFilters?: IFilter) => {
    if (!clientId) return null;
    const f = activeFilters ?? filters;
    setOrders((prev) => ({ ...prev, loading: true, error: false }));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlParcel.orders({
        clientId,
        page: pagination.filters.page as number,
        pageSize: pagination.filters.page_size as number,
        ...(f.status && { status: f.status }),
        ...(f.startDate && { dateFrom: f.startDate }),
        ...(f.endDate && { dateTo: f.endDate }),
      });
      const data = (res as IResOrders).data;
      setOrders({ data: data.data, total: data.total, loading: false, error: false });
      return data.data;
    } catch {
      setOrders((prev) => ({ ...prev, loading: false, error: true }));
      toast.error('Error al cargar órdenes de Parcel');
      return null;
    }
  };

  // Search by ML orderId → calls GET /orders/:orderId
  const handleSearch = async () => {
    const orderId = searchValue.trim();
    if (!orderId) {
      fetchOrders();
      return;
    }
    if (!clientId) return;
    setOrders((prev) => ({ ...prev, loading: true, error: false }));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlParcel.order({ orderId, clientId });
      if ('data' in res && res.success) {
        const data = (res as IResOrder).data;
        setOrders({ data: data, total: data.length, loading: false, error: false });
      } else {
        setOrders({ data: [], total: 0, loading: false, error: true });
        toast.error('No se encontró la orden');
      }
    } catch {
      setOrders({ data: [], total: 0, loading: false, error: true });
      toast.error('No se encontró la orden');
    }
  };

  const handleSearchChange = (value: string) => setSearchValue(value);

  const clearFilters = () => {
    pagination.resetFilters();
    setFilters(initialFilters);
    setSearchValue('');
    setOpenFilters(false);
    fetchOrders(initialFilters);
  };

  const applyFilters = () => {
    setOpenFilters(false);
    fetchOrders(filters);
  };

  const onClose = () => setOpenFilters(false);

  const handleRowClick = (order: Order) => {
    setSelectedOrder(order);
    handleModal('details');
  };

  const [creatingOrderIds, setCreatingOrderIds] = useState<Set<string>>(new Set());

  const handleDirectCreateOrder = async (id: string) => {
    if (!clientId) return;
    setCreatingOrderIds((prev) => new Set(prev).add(id));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlParcel.createOrder({ clientId, orderId: id });
      if ('success' in res && res.success) {
        toast.success('Orden creada en 99minutos Parcel');
        fetchOrders();
      } else {
        toast.error('Error al crear la orden');
      }
    } catch {
      toast.error('Error al crear la orden');
    } finally {
      setCreatingOrderIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDownloadLabel = (trackingId: string) => {
    setSelectedTrackingIds([trackingId]);
    handleModal('downloadLabel');
  };

  const downloadLabels = async ({
    pageSize,
    trackingIds,
  }: {
    pageSize: 'small' | 'letter' | 'zebra';
    trackingIds: string[];
  }) => {
    const http = initializeHttpProvider();
    try {
      const response = await http.orders.labels({ pageSize, trackingIds });
      if ('data' in response && !Array.isArray(response.data) && response.data.url) {
        window.open(response.data.url, '_blank');
        toast.success('Etiqueta descargada');
        handleModal('downloadLabel');
      } else {
        toast.error('Error al descargar la etiqueta');
      }
    } catch {
      toast.error('Ha ocurrido un error al intentar descargar la etiqueta');
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pagination.filters.page, pagination.filters.page_size, clientId]);

  return {
    clientId,
    orders,
    pagination,
    openFilters,
    setOpenFilters,
    filters,
    onChangeFilter,
    clearFilters,
    applyFilters,
    onClose,
    searchValue,
    handleSearchChange,
    handleSearch,
    modals,
    handleModal,
    selectedOrder,
    handleRowClick,
    handleDirectCreateOrder,
    creatingOrderIds,
    selectedTrackingIds,
    handleDownloadLabel,
    downloadLabels,
  };
};

export default useMLParcel;
